import { useEffect, useRef, useState, type ReactNode } from 'react';
import { INTL_TAG } from '@indusketch/i18n';
import { Icon } from '../../ui/Icon.js';
import { useI18n } from '../../i18n/i18n.js';

export type SortDir = 'asc' | 'desc';
type SortValue = string | number | null | undefined;

export interface SortColumn<T> {
  label: string;
  value: (row: T) => SortValue;
  /** Dates and amounts start with the largest value, text with A. */
  descFirst?: boolean;
}

export interface Sorter<K extends string> {
  key: K;
  dir: SortDir;
  toggle: (key: K) => void;
  set: (key: K, dir: SortDir) => void;
  labels: Record<K, string>;
}

/** Rows sorted by a column the user picks; empty values always go last. */
export function useSort<T, K extends string>(
  rows: readonly T[],
  columns: Record<K, SortColumn<T>>,
  initial: { key: NoInfer<K>; dir: SortDir },
): { rows: T[]; sorter: Sorter<K> } {
  const { locale } = useI18n();
  const [state, setState] = useState(initial);
  const column = columns[state.key];
  const collator = new Intl.Collator(INTL_TAG[locale], { numeric: true, sensitivity: 'base' });
  const sign = state.dir === 'asc' ? 1 : -1;
  const sorted = [...rows].sort((a, b) => {
    const x = column.value(a);
    const y = column.value(b);
    const xEmpty = x === null || x === undefined || x === '';
    const yEmpty = y === null || y === undefined || y === '';
    if (xEmpty || yEmpty) return xEmpty === yEmpty ? 0 : xEmpty ? 1 : -1;
    const diff =
      typeof x === 'number' && typeof y === 'number'
        ? x - y
        : collator.compare(String(x), String(y));
    return diff * sign;
  });
  const keys = Object.keys(columns) as K[];
  return {
    rows: sorted,
    sorter: {
      ...state,
      toggle: (key) =>
        setState((s) =>
          s.key === key
            ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
            : { key, dir: columns[key].descFirst ? 'desc' : 'asc' },
        ),
      set: (key, dir) => setState({ key, dir }),
      labels: Object.fromEntries(keys.map((k) => [k, columns[k].label])) as Record<K, string>,
    },
  };
}

/** Column heading that sorts the table on click. */
export function SortHeader<K extends string>({
  sorter,
  column,
  className,
  rowSpan,
  children,
}: {
  sorter: Sorter<K>;
  column: NoInfer<K>;
  className?: string;
  rowSpan?: number;
  children?: ReactNode;
}) {
  const active = sorter.key === column;
  return (
    <th
      className={className}
      rowSpan={rowSpan}
      aria-sort={active ? (sorter.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className={`sort-btn${active ? ' is-active' : ''}`}
        onClick={() => sorter.toggle(column)}
      >
        {children ?? sorter.labels[column]}
        <Icon
          name={active ? (sorter.dir === 'asc' ? 'chevron-up' : 'chevron-down') : 'sortable'}
          size={13}
        />
      </button>
    </th>
  );
}

/** Sorting for narrow screens, where the tables turn into cards without headings. */
export function SortSelect<K extends string>({ sorter }: { sorter: Sorter<K> }) {
  const t = useI18n().m.admin;
  const keys = Object.keys(sorter.labels) as K[];
  return (
    <span className="sort-select">
      <select
        className="input"
        value={sorter.key}
        aria-label={t.sortBy}
        onChange={(e) => sorter.set(e.target.value as K, sorter.dir)}
      >
        {keys.map((k) => (
          <option key={k} value={k}>
            {sorter.labels[k]}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="btn btn-icon"
        aria-label={sorter.dir === 'asc' ? t.sortAsc : t.sortDesc}
        title={sorter.dir === 'asc' ? t.sortAsc : t.sortDesc}
        onClick={() => sorter.set(sorter.key, sorter.dir === 'asc' ? 'desc' : 'asc')}
      >
        <Icon name={sorter.dir === 'asc' ? 'chevron-up' : 'chevron-down'} />
      </button>
    </span>
  );
}

/** "Bocholt, North Rhine-Westphalia, DE" → "Bocholt · DE"; the full text goes into a tooltip. */
export function shortLocation(location: string): string {
  const parts = location
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.length > 1 ? `${parts[0]} · ${parts.at(-1)}` : location;
}

/** Reloads while the page is visible, so "active now" stays current without a manual refresh. */
export function useAutoRefresh(reload: () => void, everyMs = 60_000): void {
  const latest = useRef(reload);
  useEffect(() => {
    latest.current = reload;
  });
  useEffect(() => {
    const tick = () => {
      if (document.visibilityState === 'visible') latest.current();
    };
    const timer = setInterval(tick, everyMs);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [everyMs]);
}
